import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBots } from '../context/BotContext';
import BRAND from '../brand';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

// ── Demo chart data ──────────────────────────────────────────
const weekData = [
  { d:'Mon', convos:42, resolved:36, revenue:820  },
  { d:'Tue', convos:68, resolved:57, revenue:1240 },
  { d:'Wed', convos:55, resolved:49, revenue:940  },
  { d:'Thu', convos:91, resolved:78, revenue:1680 },
  { d:'Fri', convos:84, resolved:72, revenue:1520 },
  { d:'Sat', convos:47, resolved:41, revenue:860  },
  { d:'Sun', convos:39, resolved:34, revenue:720  },
];

const sentimentPie = [
  { name:'Positive', value:62, color:'#10b981' },
  { name:'Neutral',  value:25, color:'#f59e0b' },
  { name:'Negative', value:13, color:'#ef4444' },
];

const atRisk = [
  { name:'Acme Corp',    issue:'No response in 48h',         risk:'red',    revenue:'$4,200' },
  { name:'TechFlow Inc', issue:'Repeated billing complaints', risk:'red',    revenue:'$2,800' },
  { name:'GreenLeaf Co', issue:'Declining engagement',        risk:'yellow', revenue:'$1,900' },
  { name:'Nova Digital', issue:'Asked about competitors',     risk:'yellow', revenue:'$3,100' },
  { name:'Bright Media', issue:'Slow response frustration',   risk:'yellow', revenue:'$870'   },
];

const riskColor = { green:'#10b981', yellow:'#f59e0b', red:'#ef4444' };
const riskBg    = { green:'rgba(16,185,129,0.1)', yellow:'rgba(245,158,11,0.1)', red:'rgba(239,68,68,0.1)' };

const topIssues = [
  { topic:'Shipping / delivery times', count:142, pct:28 },
  { topic:'Return & refund policy',    count:98,  pct:19 },
  { topic:'Pricing questions',         count:76,  pct:15 },
  { topic:'Account / login issues',    count:61,  pct:12 },
  { topic:'Integration support',       count:44,  pct:9  },
];

// ── Sidebar nav definition ───────────────────────────────────
const NAV_ITEMS = [
  { key:'overview',  icon:'▤',  label:'Overview'      },
  { key:'bots',      icon:'🤖', label:'My Bots'       },
  { key:'analytics', icon:'📊', label:'Analytics'     },
  { key:'playbooks', icon:'⚡', label:'Playbooks'     },
  { key:'risk',      icon:'🎯', label:'Risk Scoring'  },
  { key:'reports',   icon:'📋', label:'Exec Reports'  },
  { key:'settings',  icon:'⚙️', label:'Settings'      },
];

// ── Reusable small components ────────────────────────────────
const StatCard = ({ label, value, sub, color }) => (
  <div style={{ background:'#111113', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:'1.25rem 1.5rem' }}>
    <div style={{ fontSize:'0.68rem', color:'#71717a', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'0.5rem' }}>{label}</div>
    <div style={{ fontSize:'1.75rem', fontWeight:700, color:color||'#fafafa', lineHeight:1, fontFamily:'Geist Mono,monospace' }}>{value}</div>
    {sub && <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:'0.35rem' }}>{sub}</div>}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{ background:'#111113', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'1.5rem', ...style }}>
    {children}
  </div>
);

const CardHead = ({ children, sub }) => (
  <div style={{ marginBottom:'1.25rem' }}>
    <div style={{ fontWeight:600, fontSize:'0.9rem', color:'#fafafa' }}>{children}</div>
    {sub && <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:2 }}>{sub}</div>}
  </div>
);

// ════════════════════════════════════════════════════════════
// DASHBOARD
// ════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const { bots, createBot } = useBots();
  const navigate = useNavigate();

  // ── The ONE piece of state that controls which tab is shown ──
  // This was the bug: it was missing entirely before.
  const [activeTab, setActiveTab] = useState('overview');

  const [creating,      setCreating]      = useState(false);
  const [showUserMenu,  setShowUserMenu]  = useState(false);
  const [explainLoad,   setExplainLoad]   = useState(false);
  const [explanation,   setExplanation]   = useState('');
  const [settingsName,  setSettingsName]  = useState(user?.name  || '');
  const [settingsEmail, setSettingsEmail] = useState(user?.email || '');
  const [settingsSaved, setSettingsSaved] = useState(false);

  // ── Derived totals ───────────────────────────────────────
  const totalConvos   = bots.reduce((a,b) => a + (b.conversations||0), 0);
  const totalRevenue  = bots.reduce((a,b) => a + (b.revenue      ||0), 0);
  const totalResolved = bots.reduce((a,b) => a + (b.resolved     ||0), 0);
  const avgSat = bots.length
    ? (bots.reduce((a,b) => a + parseFloat(b.satisfaction||0), 0) / bots.length).toFixed(1)
    : '—';

  // ── Shared input style ───────────────────────────────────
  const inp = {
    width:'100%', padding:'0.7rem 0.9rem',
    background:'#18181b', border:'1px solid rgba(255,255,255,0.1)',
    borderRadius:8, color:'#fafafa', fontSize:'0.875rem',
    fontFamily:'Geist,sans-serif', outline:'none',
  };

  const handleCreateBot = () => {
    setCreating(true);
    setTimeout(() => {
      const bot = createBot({ companyName:(user?.name||'Your')+"'s Business" });
      setCreating(false);
      navigate(`/dashboard/bot/${bot.id}`);
    }, 600);
  };

  const handleExplain = () => {
    setExplainLoad(true);
    setExplanation('');
    setTimeout(() => {
      setExplainLoad(false);
      setExplanation('📉 Revenue dipped 12% today driven by a spike in shipping delay complaints (38% of conversations). 3 customers mentioned competitors by name. Escalation rate rose to 18% vs 9% average — primarily billing questions not in your knowledge base. Recommended: add current shipping ETAs and expand your billing FAQ.');
    }, 1800);
  };

  const handleSaveSettings = () => {
    updateUser({ name:settingsName, email:settingsEmail });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  // ════════════════════════════════════════════════════════
  // TAB PANELS — each one is a self-contained component
  // ════════════════════════════════════════════════════════

  const OverviewPanel = () => (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        <StatCard label="Total Conversations" value={totalConvos.toLocaleString()}          sub="All bots combined"     />
        <StatCard label="Revenue Attributed"  value={`$${totalRevenue.toLocaleString()}`}  sub="From chat interactions" color="#10b981" />
        <StatCard label="Resolution Rate"     value={totalConvos ? Math.round((totalResolved/totalConvos)*100)+'%' : '—'} sub="Without human help" color="#3b82f6" />
        <StatCard label="Avg Satisfaction"    value={`${avgSat}★`}                         sub="Across all bots"       color={BRAND.accent} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:'1.5rem', marginBottom:'2rem' }}>
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
            <CardHead sub="Across all bots">Conversations this week</CardHead>
            <span style={{ fontSize:'0.72rem', color:'#10b981', background:'rgba(16,185,129,0.1)', padding:'0.25rem 0.65rem', borderRadius:100 }}>↑ 18% vs last week</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={weekData} margin={{top:0,right:0,bottom:0,left:-30}}>
              <defs>
                <linearGradient id="og1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={BRAND.accent} stopOpacity={0.15}/>
                  <stop offset="95%" stopColor={BRAND.accent} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="og2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.12}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="d" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
              <Area type="monotone" dataKey="convos"   stroke={BRAND.accent} strokeWidth={2} fill="url(#og1)" name="Conversations"/>
              <Area type="monotone" dataKey="resolved" stroke="#10b981"      strokeWidth={2} fill="url(#og2)" name="Resolved"/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardHead sub="$ per day this week">Revenue Attributed</CardHead>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weekData} margin={{top:0,right:0,bottom:0,left:-30}}>
              <XAxis dataKey="d" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
              <Bar dataKey="revenue" fill={BRAND.accent} radius={[4,4,0,0]} name="Revenue ($)"/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:'1.5rem', marginBottom:'2rem' }}>
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
            <span style={{ fontWeight:600, fontSize:'0.9rem' }}>Your Bots</span>
            <button onClick={handleCreateBot} style={{ background:`rgba(245,158,11,0.1)`, color:BRAND.accent, border:'none', borderRadius:6, padding:'0.3rem 0.75rem', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', fontFamily:'Geist,sans-serif' }}>+ Add</button>
          </div>
          {bots.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 2rem', color:'#71717a' }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.75rem' }}>🤖</div>
              <div style={{ fontSize:'0.875rem', color:'#a1a1aa', marginBottom:'0.5rem' }}>No bots yet</div>
              <div style={{ fontSize:'0.8rem' }}>Click "+ New Bot" above to create your first AI assistant</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {bots.map(bot => (
                <div key={bot.id} onClick={() => navigate(`/dashboard/bot/${bot.id}`)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', background:'#18181b', borderRadius:10, cursor:'pointer', border:'1px solid rgba(255,255,255,0.04)', transition:'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='rgba(245,158,11,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.04)'}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.85rem' }}>
                    <div style={{ width:38, height:38, borderRadius:10, background:'rgba(245,158,11,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem' }}>🤖</div>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{bot.name}</div>
                      <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>{bot.companyName||'No company set'}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'1.25rem' }}>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:'0.875rem', fontWeight:600, fontFamily:'Geist Mono,monospace' }}>{(bot.conversations||0).toLocaleString()}</div>
                      <div style={{ fontSize:'0.68rem', color:'#71717a' }}>convos</div>
                    </div>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:bot.active?'#10b981':'#ef4444', boxShadow:bot.active?'0 0 6px rgba(16,185,129,0.5)':'none' }}/>
                    <span style={{ color:'#71717a', fontSize:'0.8rem' }}>→</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <CardHead sub="AI churn prediction — top 5">⚠️ Customers at Risk</CardHead>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem' }}>
            {atRisk.map((c,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem', background:riskBg[c.risk], border:`1px solid ${riskColor[c.risk]}22`, borderRadius:8 }}>
                <div>
                  <div style={{ fontSize:'0.82rem', fontWeight:600, color:'#fafafa' }}>{c.name}</div>
                  <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>{c.issue}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:riskColor[c.risk], textTransform:'uppercase', letterSpacing:'0.5px' }}>{c.risk}</div>
                  <div style={{ fontSize:'0.7rem', color:'#71717a', marginTop:2 }}>{c.revenue}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ background:'#0f0d00', border:'1px solid rgba(245,158,11,0.2)', borderRadius:14, padding:'1.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
            <span style={{ fontSize:'1.1rem' }}>📋</span>
            <div>
              <div style={{ fontWeight:600, fontSize:'0.9rem' }}>Today's Executive Summary</div>
              <div style={{ fontSize:'0.72rem', color:'#71717a' }}>AI-generated · Updated 3 minutes ago</div>
            </div>
          </div>
          <span style={{ fontSize:'0.72rem', color:BRAND.accent, background:'rgba(245,158,11,0.1)', padding:'0.25rem 0.65rem', borderRadius:100, border:'1px solid rgba(245,158,11,0.2)' }}>AI Report</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1.25rem' }}>
          {[
            { label:'What happened', icon:'📈', text:'Conversations up 18% vs last week. Revenue from chat grew $1,240. Top issue: shipping timeline questions (23% of queries).' },
            { label:'Why it happened', icon:'🔍', text:'Shipping delays triggered a spike in "where\'s my order" queries. 2 customers mentioned competitors. Bot resolved 84% without escalation.' },
            { label:'What to do next', icon:'⚡', text:'Update knowledge base with current shipping ETAs. Add proactive delay notifications. Review 2 competitor mentions for retention risk.' },
          ].map(item => (
            <div key={item.label} style={{ background:'rgba(245,158,11,0.05)', borderRadius:10, padding:'1rem' }}>
              <div style={{ fontSize:'0.68rem', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:BRAND.accent, marginBottom:'0.5rem' }}>{item.icon} {item.label}</div>
              <p style={{ fontSize:'0.82rem', color:'#a1a1aa', lineHeight:1.6, fontWeight:300 }}>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const BotsPanel = () => (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <div>
          <h2 style={{ fontSize:'1.1rem', fontWeight:600 }}>My Bots</h2>
          <p style={{ fontSize:'0.8rem', color:'#71717a', marginTop:2 }}>Manage all your AI assistants</p>
        </div>
        <button onClick={handleCreateBot} disabled={creating}
          style={{ background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.65rem 1.4rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.875rem', opacity:creating?0.7:1 }}>
          {creating ? 'Creating...' : '+ Create New Bot'}
        </button>
      </div>
      {bots.length === 0 ? (
        <div style={{ background:'#111113', border:'2px dashed rgba(255,255,255,0.1)', borderRadius:16, padding:'5rem 2rem', textAlign:'center', color:'#71717a' }}>
          <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🤖</div>
          <div style={{ fontSize:'1rem', color:'#a1a1aa', marginBottom:'0.5rem', fontWeight:600 }}>No bots yet</div>
          <div style={{ fontSize:'0.85rem', marginBottom:'1.5rem' }}>Create your first AI support bot and get it live in under an hour</div>
          <button onClick={handleCreateBot} style={{ background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.75rem 1.75rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif' }}>Create your first bot →</button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'1rem' }}>
          {bots.map(bot => (
            <div key={bot.id} onClick={() => navigate(`/dashboard/bot/${bot.id}`)}
              style={{ background:'#111113', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'1.5rem', cursor:'pointer', transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(245,158,11,0.3)'; e.currentTarget.style.transform='translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.transform='none'; }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ width:42, height:42, borderRadius:12, background:'rgba(245,158,11,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem' }}>🤖</div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:'0.95rem' }}>{bot.name}</div>
                    <div style={{ fontSize:'0.72rem', color:'#71717a' }}>{bot.companyName||'No company set'}</div>
                  </div>
                </div>
                <div style={{ fontSize:'0.72rem', color:bot.active?'#10b981':'#ef4444', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:'currentColor' }}/>{bot.active?'Live':'Paused'}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem', marginBottom:'1rem' }}>
                {[
                  { l:'Convos',   v:(bot.conversations||0).toLocaleString() },
                  { l:'Resolved', v:`${bot.conversations ? Math.round(((bot.resolved||0)/(bot.conversations||1))*100) : 0}%` },
                  { l:'Revenue',  v:`$${(bot.revenue||0).toLocaleString()}` },
                ].map(s => (
                  <div key={s.l} style={{ background:'#18181b', borderRadius:8, padding:'0.6rem', textAlign:'center' }}>
                    <div style={{ fontSize:'0.85rem', fontWeight:700, fontFamily:'Geist Mono,monospace' }}>{s.v}</div>
                    <div style={{ fontSize:'0.65rem', color:'#71717a', marginTop:2 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:'0.72rem', color:BRAND.accent }}>Configure bot →</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const AnalyticsPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Analytics</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Performance across all your bots</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        <StatCard label="Total Conversations" value={totalConvos.toLocaleString()} />
        <StatCard label="Resolved by AI"      value={totalResolved.toLocaleString()} color="#10b981" />
        <StatCard label="Revenue Attributed"  value={`$${totalRevenue.toLocaleString()}`} color="#3b82f6" />
        <StatCard label="Avg Satisfaction"    value={`${avgSat}/5`} color={BRAND.accent} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'1.5rem', marginBottom:'1.5rem' }}>
        <Card>
          <CardHead sub="Conversations vs resolved per day">7-Day Performance</CardHead>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={weekData} margin={{top:0,right:0,bottom:0,left:-30}}>
              <defs>
                <linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={BRAND.accent} stopOpacity={0.15}/>
                  <stop offset="95%" stopColor={BRAND.accent} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="d" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
              <Area type="monotone" dataKey="convos"   stroke={BRAND.accent} strokeWidth={2} fill="url(#ag1)" name="Conversations"/>
              <Area type="monotone" dataKey="resolved" stroke="#10b981"      strokeWidth={2} fill="none" strokeDasharray="4 2" name="Resolved"/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <CardHead sub="Overall sentiment breakdown">Customer Sentiment</CardHead>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={sentimentPie} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none">
                {sentimentPie.map((e,i) => <Cell key={i} fill={e.color}/>)}
              </Pie>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', marginTop:'0.75rem' }}>
            {sentimentPie.map(s => (
              <div key={s.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'0.78rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.45rem' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:s.color }}/>
                  <span style={{ color:'#a1a1aa' }}>{s.name}</span>
                </div>
                <span style={{ fontWeight:600 }}>{s.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card>
        <CardHead sub="Most common customer questions">Top Issues</CardHead>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          {topIssues.map((issue,i) => (
            <div key={i}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:'0.35rem' }}>
                <span style={{ color:'#fafafa' }}>{issue.topic}</span>
                <span style={{ color:'#71717a' }}>{issue.count} convos · {issue.pct}%</span>
              </div>
              <div style={{ height:5, background:'#1e1e24', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${issue.pct}%`, background:BRAND.accent, borderRadius:3, opacity:0.8 }}/>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const PlaybooksPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Playbooks</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Automated IF → THEN rules that run across all bots</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1.5rem' }}>
        {[
          { icon:'😔', trigger:'Sentiment < 3 stars',            action:'Auto-send apology + 10% discount', active:true,  runs:47 },
          { icon:'🔄', trigger:'Keyword: "refund" or "cancel"',  action:'Escalate to human immediately',    active:true,  runs:23 },
          { icon:'🏆', trigger:'Customer mentions a competitor', action:'Highlight top 3 differentiators',  active:false, runs:8  },
          { icon:'🎫', trigger:'Same complaint 2+ times',        action:'Create ticket + notify team',      active:true,  runs:14 },
        ].map((pb,i) => (
          <div key={i} style={{ background:pb.active?'rgba(245,158,11,0.05)':'#111113', border:`1px solid ${pb.active?'rgba(245,158,11,0.2)':'rgba(255,255,255,0.07)'}`, borderRadius:12, padding:'1.25rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem' }}>
              <span style={{ fontSize:'1.3rem' }}>{pb.icon}</span>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <span style={{ fontSize:'0.7rem', color:'#71717a' }}>{pb.runs} runs</span>
                <div style={{ width:36, height:20, borderRadius:10, background:pb.active?'#10b981':'#27272a', cursor:'pointer', position:'relative' }}>
                  <div style={{ position:'absolute', top:2, left:pb.active?18:2, width:16, height:16, borderRadius:'50%', background:'#fafafa', transition:'left 0.2s' }}/>
                </div>
              </div>
            </div>
            <div style={{ fontSize:'0.75rem', color:'#71717a', marginBottom:'0.2rem' }}>IF: <span style={{ color:BRAND.accent, fontFamily:'Geist Mono,monospace', fontSize:'0.72rem' }}>{pb.trigger}</span></div>
            <div style={{ fontSize:'0.75rem', color:'#71717a' }}>THEN: <span style={{ color:'#10b981', fontSize:'0.72rem' }}>{pb.action}</span></div>
          </div>
        ))}
      </div>
      <Card>
        <CardHead sub="Define a new automated rule">Create Playbook</CardHead>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Trigger (IF)</label>
            <input style={inp} placeholder='e.g. sentiment < 3 or keyword: refund' onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
          </div>
          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Action (THEN)</label>
            <input style={inp} placeholder='e.g. Send apology + discount code' onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
          </div>
        </div>
        <button style={{ background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.6rem 1.25rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.85rem' }}>Add Playbook</button>
      </Card>
    </div>
  );

  const RiskPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Customer Risk Scoring</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>AI churn prediction — sorted by revenue at risk</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        {[
          { label:'High Risk',   count:2,  color:'#ef4444', bg:'rgba(239,68,68,0.1)'   },
          { label:'Medium Risk', count:3,  color:'#f59e0b', bg:'rgba(245,158,11,0.1)'  },
          { label:'Healthy',     count:12, color:'#10b981', bg:'rgba(16,185,129,0.1)'  },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.color}22`, borderRadius:12, padding:'1.25rem', textAlign:'center' }}>
            <div style={{ fontSize:'2rem', fontWeight:700, color:s.color, fontFamily:'Geist Mono,monospace' }}>{s.count}</div>
            <div style={{ fontSize:'0.78rem', color:'#a1a1aa', marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <Card>
        <CardHead sub="Sorted by revenue at risk">All Customers</CardHead>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {[...atRisk,
            { name:'Stellar Goods', issue:'Healthy engagement', risk:'green', revenue:'$5,400' },
            { name:'PeakFlow Ltd',  issue:'Regular — positive sentiment', risk:'green', revenue:'$3,200' },
          ].map((c,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', background:'#18181b', border:`1px solid ${riskColor[c.risk]}18`, borderRadius:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:riskColor[c.risk], flexShrink:0 }}/>
                <div>
                  <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{c.name}</div>
                  <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>{c.issue}</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
                <span style={{ fontSize:'0.82rem', fontWeight:600 }}>{c.revenue}</span>
                <span style={{ fontSize:'0.7rem', fontWeight:700, color:riskColor[c.risk], background:riskBg[c.risk], padding:'0.2rem 0.55rem', borderRadius:4, textTransform:'uppercase', letterSpacing:'0.5px' }}>{c.risk}</span>
                {c.risk !== 'green' && (
                  <button style={{ background:'rgba(245,158,11,0.1)', color:BRAND.accent, border:'1px solid rgba(245,158,11,0.2)', borderRadius:6, padding:'0.25rem 0.65rem', fontSize:'0.72rem', fontWeight:600, cursor:'pointer', fontFamily:'Geist,sans-serif' }}>Take action</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const ReportsPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Executive Reports</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>AI-generated summaries of what's happening and why</p>
      <div style={{ background:'#0f0d00', border:'1px solid rgba(245,158,11,0.2)', borderRadius:14, padding:'1.5rem', marginBottom:'1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: explainLoad||explanation?'1rem':0 }}>
          <div>
            <div style={{ fontWeight:600, fontSize:'0.95rem' }}>🔍 Explain the Drop</div>
            <div style={{ fontSize:'0.75rem', color:'#71717a', marginTop:2 }}>AI analyzes your data and explains what's hurting performance</div>
          </div>
          <button onClick={handleExplain} disabled={explainLoad}
            style={{ background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.6rem 1.25rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.85rem', opacity:explainLoad?0.7:1 }}>
            {explainLoad ? '⟳ Analyzing...' : 'Why did revenue drop?'}
          </button>
        </div>
        {explainLoad && <div style={{ height:3, background:'rgba(245,158,11,0.1)', borderRadius:3, overflow:'hidden' }}><div style={{ height:'100%', background:BRAND.accent, borderRadius:3, animation:'progress 1.8s ease-in-out' }}/></div>}
        {explanation && <div style={{ fontSize:'0.875rem', color:'#a1a1aa', lineHeight:1.65, background:'rgba(245,158,11,0.05)', borderRadius:10, padding:'1rem', marginTop:'0.5rem' }}>{explanation}</div>}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1.25rem', marginBottom:'1.5rem' }}>
        {[
          { label:'What happened', icon:'📈', text:'Conversations up 18% vs last week. Revenue from chat grew $1,240. Top issue: shipping timeline questions (23% of queries).' },
          { label:'Why it happened', icon:'🔍', text:'Shipping delays triggered a spike in order queries. 2 customers mentioned competitors. Bot resolved 84% without escalation.' },
          { label:'What to do next', icon:'⚡', text:'Update knowledge base with current shipping ETAs. Add proactive delay notifications. Review competitor mentions.' },
        ].map(item => (
          <Card key={item.label}>
            <div style={{ fontSize:'0.68rem', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', color:BRAND.accent, marginBottom:'0.75rem' }}>{item.icon} {item.label}</div>
            <p style={{ fontSize:'0.82rem', color:'#a1a1aa', lineHeight:1.65, fontWeight:300 }}>{item.text}</p>
          </Card>
        ))}
      </div>
      <Card>
        <CardHead sub="Configure when and where to receive AI reports">Report Schedule</CardHead>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Send reports to</label>
            <input style={inp} defaultValue={user?.email} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
          </div>
          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Frequency</label>
            <select style={{...inp,cursor:'pointer'}}>
              <option>Daily at 8:00 AM</option>
              <option>Weekly on Monday</option>
              <option>Monthly on the 1st</option>
            </select>
          </div>
        </div>
        <button style={{ marginTop:'1rem', background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.6rem 1.25rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.85rem' }}>Save schedule</button>
      </Card>
      <style>{`@keyframes progress{0%{width:0%}100%{width:100%}}`}</style>
    </div>
  );

  const SettingsPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Settings</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Manage your account and preferences</p>
      <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
        <Card>
          <CardHead>Account Details</CardHead>
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            <div>
              <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Full name</label>
              <input style={inp} value={settingsName} onChange={e=>setSettingsName(e.target.value)} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            </div>
            <div>
              <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Email address</label>
              <input type="email" style={inp} value={settingsEmail} onChange={e=>setSettingsEmail(e.target.value)} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <button onClick={handleSaveSettings} style={{ background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.65rem 1.4rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.875rem' }}>Save changes</button>
              {settingsSaved && <span style={{ fontSize:'0.82rem', color:'#10b981' }}>✓ Saved successfully</span>}
            </div>
          </div>
        </Card>
        <Card>
          <CardHead sub="Your current subscription">Plan & Billing</CardHead>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:10, marginBottom:'1rem' }}>
            <div>
              <div style={{ fontWeight:600, fontSize:'0.9rem', textTransform:'capitalize' }}>{user?.plan||'Starter'} Plan</div>
              <div style={{ fontSize:'0.75rem', color:'#71717a', marginTop:2 }}>Renews monthly · Manage in Stripe</div>
            </div>
            <span style={{ fontSize:'0.72rem', color:BRAND.accent, background:'rgba(245,158,11,0.1)', padding:'0.25rem 0.65rem', borderRadius:100, border:'1px solid rgba(245,158,11,0.2)', textTransform:'capitalize' }}>{user?.plan||'starter'}</span>
          </div>
          <button style={{ background:'transparent', color:'#a1a1aa', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'0.6rem 1.25rem', cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.85rem' }}>Manage billing →</button>
        </Card>
        <Card>
          <CardHead sub="Sign out of your account">Session</CardHead>
          <button onClick={logout} style={{ background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, padding:'0.65rem 1.4rem', cursor:'pointer', fontFamily:'Geist,sans-serif', fontSize:'0.875rem', fontWeight:600 }}>Sign out</button>
        </Card>
      </div>
    </div>
  );

  // ── Render the right panel based on activeTab ────────────
  const renderPanel = () => {
    switch (activeTab) {
      case 'overview':  return <OverviewPanel/>;
      case 'bots':      return <BotsPanel/>;
      case 'analytics': return <AnalyticsPanel/>;
      case 'playbooks': return <PlaybooksPanel/>;
      case 'risk':      return <RiskPanel/>;
      case 'reports':   return <ReportsPanel/>;
      case 'settings':  return <SettingsPanel/>;
      default:          return <OverviewPanel/>;
    }
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:'100vh', background:'#09090b', fontFamily:'Geist,sans-serif', display:'flex' }}>

      {/* SIDEBAR */}
      <aside style={{ width:220, background:'#0d0d10', borderRight:'1px solid rgba(255,255,255,0.06)', display:'flex', flexDirection:'column', flexShrink:0, position:'sticky', top:0, height:'100vh' }}>

        <div style={{ padding:'1.5rem 1.25rem', borderBottom:'1px solid rgba(255,255,255,0.06)', flexShrink:0 }}>
          <div style={{ fontFamily:'Instrument Serif,serif', fontSize:'1.3rem', color:'#fafafa' }}>
            {BRAND.name}<span style={{ color:BRAND.accent }}>.</span>
          </div>
        </div>

        <nav style={{ flex:1, padding:'0.75rem 0', overflowY:'auto' }}>
          {NAV_ITEMS.map(item => {
            const active = activeTab === item.key;
            return (
              /* KEY FIX: use <button> not <div>, and call setActiveTab onClick */
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                style={{
                  display:'flex', alignItems:'center', gap:'0.65rem',
                  width:'calc(100% - 1.5rem)', margin:'0.1rem 0.75rem',
                  padding:'0.6rem 1rem', borderRadius:8, cursor:'pointer',
                  border:'none', textAlign:'left',
                  background: active ? `rgba(245,158,11,0.12)` : 'transparent',
                  color:  active ? BRAND.accent : '#71717a',
                  fontSize:'0.85rem', fontWeight: active ? 600 : 400,
                  fontFamily:'Geist,sans-serif', transition:'all 0.15s',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background='rgba(255,255,255,0.04)'; e.currentTarget.style.color='#fafafa'; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#71717a'; }}}
              >
                <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User section */}
        <div style={{ padding:'1rem 1.25rem', borderTop:'1px solid rgba(255,255,255,0.06)', flexShrink:0, position:'relative' }}>
          {showUserMenu && (
            <div style={{ position:'absolute', bottom:'100%', left:'0.75rem', right:'0.75rem', background:'#18181b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, overflow:'hidden', marginBottom:4, zIndex:50 }}>
              <div onClick={() => { setActiveTab('settings'); setShowUserMenu(false); }}
                style={{ padding:'0.65rem 0.85rem', fontSize:'0.82rem', color:'#fafafa', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.06)' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                Account settings
              </div>
              <div onClick={logout}
                style={{ padding:'0.65rem 0.85rem', fontSize:'0.82rem', color:'#ef4444', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.06)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                Sign out
              </div>
            </div>
          )}
          <button onClick={() => setShowUserMenu(p=>!p)}
            style={{ display:'flex', alignItems:'center', gap:'0.65rem', width:'100%', padding:'0.5rem', borderRadius:8, cursor:'pointer', background:'transparent', border:'none', textAlign:'left', fontFamily:'Geist,sans-serif' }}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(245,158,11,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:700, color:BRAND.accent, flexShrink:0 }}>
              {user?.name?.charAt(0).toUpperCase()||'?'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'0.8rem', fontWeight:500, color:'#fafafa', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'capitalize' }}>{user?.plan||'starter'} plan</div>
            </div>
            <span style={{ color:'#71717a', fontSize:'0.6rem' }}>▲</span>
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex:1, overflow:'auto' }}>
        <div style={{ padding:'1.25rem 2rem', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#09090b', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <h1 style={{ fontSize:'1.1rem', fontWeight:600, color:'#fafafa' }}>
              {NAV_ITEMS.find(n=>n.key===activeTab)?.label || 'Dashboard'}
            </h1>
            <p style={{ fontSize:'0.75rem', color:'#71717a', marginTop:1 }}>Welcome back, {user?.name?.split(' ')[0]} 👋</p>
          </div>
          <button onClick={handleCreateBot} disabled={creating}
            style={{ display:'flex', alignItems:'center', gap:'0.5rem', background:BRAND.accent, color:BRAND.accentText, border:'none', borderRadius:8, padding:'0.6rem 1.25rem', fontSize:'0.85rem', fontWeight:700, cursor:'pointer', fontFamily:'Geist,sans-serif', opacity:creating?0.7:1 }}>
            {creating ? 'Creating...' : '+ New Bot'}
          </button>
        </div>

        <div style={{ padding:'2rem' }}>
          {renderPanel()}
        </div>
      </main>
    </div>
  );
}